import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import FormInput from "../../components/ui/FormInput";
import UsersImg from "../../assets/Users.png";
import cateMakerter from "../../assets/cate-maketer.png";
import cateDigital from "../../assets/cate-digital.png";
import cateFlim from "../../assets/cate-flim.png";
import iconLogo from "../../assets/icon-logo.png";

// Import new reusable components
import StepProgress from "../../components/dashboard/StepProgress";
import ErrorNotification from "../../components/dashboard/ErrorNotification";
import NotificationOverlay from "../../components/dashboard/NotificationOverlay";
import { TeamAPI, UserAPI } from "../../services/api";
import { API_URL } from "../../config/api";
import { useNavigate } from "react-router-dom";
import { COMPETITION_YEAR } from "../../config/competition";

// Types for registration data
interface TeamData {
  name: string;
  manifesto: string;
  teamId?: string;
  league?: "student" | "professional";
  category?: "digital" | "film" | "marketers";
}

interface RegistrationState {
  flow: "create" | "join" | null; // Which flow the user chose
  step: number; // Current step in the flow
  teamData: TeamData;
  searchResults?: any; // For join team search results
}

export function RegistrationDashboard() {
  const navigate = useNavigate();
  const [registration, setRegistration] = useState<RegistrationState>({
    flow: null,
    step: 1,
    teamData: {
      name: "",
      manifesto: "",
    },
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [teamNameAvailable, setTeamNameAvailable] = useState<boolean | null>(
    null,
  );
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null,
  );
  const [copySuccess, setCopySuccess] = useState(false);
  const [showGuidelinesPopup, setShowGuidelinesPopup] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);
  const [userTeams, setUserTeams] = useState<any[]>([]);
  const [canCreateMore, setCanCreateMore] = useState(true);
  const [registrationStatus, setRegistrationStatus] = useState<{
    can_register: boolean;
    status: string;
    message: string;
    registration_phase?: any;
  } | null>(null);
  const [registrationLoading, setRegistrationLoading] = useState(true);
  const [ageEligibility, setAgeEligibility] = useState<{
    is_eligible: boolean;
    message: string;
    birth_date?: string;
  } | null>(null);
  const [ageEligibilityLoading, setAgeEligibilityLoading] = useState(true);
  const [nationalityEligibility, setNationalityEligibility] = useState<{
    is_eligible: boolean;
    message: string;
    nationality?: string;
  } | null>(null);
  const [nationalityEligibilityLoading, setNationalityEligibilityLoading] =
    useState(true);
  const [userCompany, setUserCompany] = useState<string | null>(null);
  const [userCompanyLoading, setUserCompanyLoading] = useState(true);
  const [userGraduationDate, setUserGraduationDate] = useState<string | null>(
    null,
  );
  const [userGraduationLoading, setUserGraduationLoading] = useState(true);

  // League and Category mappings (should match backend database IDs)
  const leagueMap = {
    student: 1,
    professional: 2,
  };

  const categoryMap = {
    digital: 1,
    film: 2,
    marketers: 3,
  };

  // Helper function to check if user is a freelancer
  const isFreelancer = (): boolean => {
    return userCompany?.toLowerCase() === "freelancer";
  };

  // Helper function to check if user graduates before the competition year (should use Industry Practitioner)
  const shouldUseIndustryPractitioner = (): boolean => {
    console.log("🔍 Checking graduation date:", userGraduationDate);

    if (!userGraduationDate || userGraduationDate.trim() === "") {
      console.log("❌ No graduation date found");
      return false;
    }

    try {
      let graduationDate: Date | null = null;

      // Try multiple date formats
      if (userGraduationDate.includes("/")) {
        // DD/MM/YYYY format
        const parts = userGraduationDate.split("/");
        console.log("📅 Date parts (DD/MM/YYYY):", parts);

        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10);
          const year = parseInt(parts[2], 10);

          console.log("📊 Parsed values:", { day, month, year });
          graduationDate = new Date(year, month - 1, day);
        }
      } else if (userGraduationDate.includes("-")) {
        // YYYY-MM-DD format (ISO format)
        console.log("📅 Trying ISO date format:", userGraduationDate);
        graduationDate = new Date(userGraduationDate);
      } else {
        // Try direct parsing
        console.log("📅 Trying direct date parsing:", userGraduationDate);
        graduationDate = new Date(userGraduationDate);
      }

      if (graduationDate && !isNaN(graduationDate.getTime())) {
        const targetDate = new Date(COMPETITION_YEAR, 0, 1); // January 1 of the competition year

        console.log("📅 Graduation date:", graduationDate);
        console.log("📅 Target date:", targetDate);
        console.log(
          `⏰ Is before ${COMPETITION_YEAR}?`,
          graduationDate < targetDate,
        );

        const result = graduationDate < targetDate;
        console.log("✅ Final result:", result);
        return result;
      } else {
        console.log("❌ Invalid date object created");
        return false;
      }
    } catch (error) {
      console.log("❌ Parsing error:", error);
    }
    return false;
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Load user teams data on component mount
  const loadUserTeams = async () => {
    try {
      const response = await TeamAPI.getMyTeams();
      if (response.success) {
        setUserTeams(response.teams || []);
        setCanCreateMore(response.can_create_more || false);
      }
    } catch (err) {
      console.error("Failed to load user teams:", err);
      // Don't show error for this, as it's background data
    }
  };

  // Check age eligibility based on birth date
  const checkAgeEligibility = async () => {
    setAgeEligibilityLoading(true);
    try {
      // Fetch user profile to get birth date
      const response = await UserAPI.getProfile();

      if (response.success && response.user?.date_of_birth) {
        const birthDate = new Date(response.user.date_of_birth);
        const cutoffDate = new Date("1996-06-25"); // June 25, 1996

        if (birthDate < cutoffDate) {
          setAgeEligibility({
            is_eligible: false,
            message:
              "You are not eligible to participate in this competition due to age requirements.<br/>Participants must be born on or after June 25, 1996.",
            birth_date: response.user.date_of_birth,
          });
        } else {
          setAgeEligibility({
            is_eligible: true,
            message: "You meet the age requirements for this competition.",
            birth_date: response.user.date_of_birth,
          });
        }
      } else {
        // If no birth date is provided, consider ineligible until profile is complete
        setAgeEligibility({
          is_eligible: false,
          message:
            "Please complete your profile with your date of birth to verify age eligibility.",
        });
      }
    } catch (err) {
      console.error("Error checking age eligibility:", err);
      setAgeEligibility({
        is_eligible: false,
        message:
          "Unable to verify age eligibility. Please complete your profile and try again.",
      });
    } finally {
      setAgeEligibilityLoading(false);
    }
  };

  // Check nationality eligibility
  const checkNationalityEligibility = async () => {
    setNationalityEligibilityLoading(true);
    try {
      // Fetch user profile to get nationality
      const response = await UserAPI.getProfile();

      if (response.success && response.user?.nationality) {
        if (response.user.nationality === "vietnamese") {
          setNationalityEligibility({
            is_eligible: true,
            message:
              "You meet the nationality requirements for this competition.",
            nationality: response.user.nationality,
          });
        } else {
          setNationalityEligibility({
            is_eligible: false,
            message:
              "You are not eligible to participate in this competition due to nationality requirements.<br/>Only Vietnamese participants are allowed for this competition.",
            nationality: response.user.nationality,
          });
        }
      } else {
        // If no nationality is provided, consider ineligible until profile is complete
        setNationalityEligibility({
          is_eligible: false,
          message:
            "Please complete your profile with your nationality to verify eligibility.",
        });
      }
    } catch (err) {
      console.error("Error checking nationality eligibility:", err);
      setNationalityEligibility({
        is_eligible: false,
        message:
          "Unable to verify nationality eligibility. Please complete your profile and try again.",
      });
    } finally {
      setNationalityEligibilityLoading(false);
    }
  };

  // Check user company information
  const checkUserCompany = async () => {
    setUserCompanyLoading(true);
    try {
      // Fetch user profile to get company information
      const response = await UserAPI.getProfile();

      if (response.success && response.user?.company_name) {
        setUserCompany(response.user.company_name);
      } else {
        setUserCompany(null);
      }
    } catch (err) {
      console.error("Error checking user company:", err);
      setUserCompany(null);
    } finally {
      setUserCompanyLoading(false);
    }
  };

  // Check user graduation date
  const checkUserGraduation = async () => {
    console.log("🔄 Loading user graduation date...");
    setUserGraduationLoading(true);
    try {
      // Fetch user profile to get graduation date
      const response = await UserAPI.getProfile();
      console.log("📝 Full profile response:", response);
      console.log("📝 User object:", response.user);

      // Check multiple possible field names
      const graduationDate =
        response.user?.graduation_date ||
        response.user?.graduationDate ||
        response.user?.graduation ||
        null;

      console.log("🔍 Graduation date field check:", {
        graduation_date: response.user?.graduation_date,
        graduationDate: response.user?.graduationDate,
        graduation: response.user?.graduation,
        found: graduationDate,
      });

      if (response.success && graduationDate) {
        console.log("✅ Found graduation date:", graduationDate);
        setUserGraduationDate(graduationDate);
      } else {
        console.log("❌ No graduation date found in profile");
        console.log(
          "📋 Available user fields:",
          Object.keys(response.user || {}),
        );
        setUserGraduationDate(null);
      }
    } catch (err) {
      console.error("Error checking user graduation date:", err);
      setUserGraduationDate(null);
    } finally {
      setUserGraduationLoading(false);
    }
  };

  // Check registration status from competition phases
  const checkRegistrationStatus = async () => {
    setRegistrationLoading(true);
    try {
      const response = await fetch(
        `${API_URL.BASE_API}competition-phases/registration-status`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setRegistrationStatus(result.data);
        } else {
          console.error("Failed to get registration status:", result.message);
          // Set a default "closed" status if API fails
          setRegistrationStatus({
            can_register: false,
            status: "unavailable",
            message:
              "Unable to verify registration status. Please try again later.",
          });
        }
      } else {
        throw new Error("Failed to fetch registration status");
      }
    } catch (err) {
      console.error("Error checking registration status:", err);
      // Set a default "closed" status if API fails
      setRegistrationStatus({
        can_register: false,
        status: "unavailable",
        message:
          "Unable to verify registration status. Please try again later.",
      });
    } finally {
      setRegistrationLoading(false);
    }
  };

  useEffect(() => {
    loadUserTeams();
    checkRegistrationStatus();
    checkAgeEligibility();
    checkNationalityEligibility();
    checkUserCompany();
    checkUserGraduation();
  }, []);

  // Generate dynamic registration status message
  const getRegistrationStatusMessage = () => {
    const teamCount = userTeams.length;
    const maxTeams = 2; // Maximum teams per user
    const availableRegistrations = maxTeams - teamCount;

    if (teamCount === 0) {
      return (
        <div className="text-sm text-gray-600 mb-4 text-left">
          You have <strong>0</strong> active registrations and have{" "}
          <strong>{maxTeams}</strong> registrations available.
        </div>
      );
    } else if (teamCount === 1) {
      const teamName = userTeams[0]?.team_name || "Unknown Team";
      return (
        <div className="text-sm text-gray-600 mb-4 text-left">
          You've joined Team <strong>{teamName}</strong> and can join{" "}
          <strong>{availableRegistrations}</strong> more team
          {availableRegistrations !== 1 ? "s" : ""} in the same league if you
          want.
        </div>
      );
    } else {
      const teamNames = userTeams
        .map((team) => team.team_name || "Unknown Team")
        .join(", ");
      return (
        <div className="text-sm text-gray-600 mb-4 text-left">
          You've joined Teams <strong>{teamNames}</strong> and have reached the
          maximum team limit for this league.
        </div>
      );
    }
  };

  // Helper function to get step labels based on flow
  const getStepLabels = () => {
    if (registration.flow === "create") {
      return ["Create new team", "League", "Categories", "Finish"];
    } else if (registration.flow === "join") {
      return ["Find & Join your team", "Finish"];
    }
    return [];
  };

  // Helper function to get progress percentage
  const getProgressPercent = () => {
    if (registration.flow === "create") {
      switch (registration.step) {
        case 1:
          return 25;
        case 2:
          return 50;
        case 3:
          return 75;
        case 4:
          return 100;
        default:
          return 25;
      }
    } else if (registration.flow === "join") {
      return registration.step === 1 ? 50 : 100;
    }
    return 0;
  };

  // Helper function to get total steps
  const getTotalSteps = () => {
    return registration.flow === "create" ? 4 : 2;
  };

  // Navigation functions
  const nextStep = () => {
    // Validate current step before allowing progression
    const validation = validateCurrentStep();

    if (!validation.isValid) {
      setError(validation.errors.join(". "));
      return;
    }

    setError(""); // Clear any previous errors
    const totalSteps = getTotalSteps();
    if (registration.step < totalSteps) {
      setRegistration((prev) => ({ ...prev, step: prev.step + 1 }));
    }
  };

  const prevStep = () => {
    if (registration.step > 1) {
      setRegistration((prev) => ({ ...prev, step: prev.step - 1 }));
    } else if (registration.flow) {
      // Go back to initial step
      setRegistration((prev) => ({ ...prev, flow: null, step: 1 }));
    }
  };

  // Flow selection functions
  const selectCreateTeam = () => {
    // Check if registration is allowed
    if (!registrationStatus?.can_register) {
      setError(
        registrationStatus?.message ||
          "Team registration is not available at this time.",
      );
      return;
    }

    // Check age eligibility
    if (!ageEligibility?.is_eligible) {
      setError(
        ageEligibility?.message ||
          "You are not eligible to participate due to age requirements.",
      );
      return;
    }

    // Check nationality eligibility
    if (!nationalityEligibility?.is_eligible) {
      setError(
        nationalityEligibility?.message ||
          "You are not eligible to participate due to nationality requirements.",
      );
      return;
    }

    setRegistration((prev) => ({ ...prev, flow: "create", step: 1 }));
  };

  const selectJoinTeam = () => {
    // Check if registration is allowed
    if (!registrationStatus?.can_register) {
      setError(
        registrationStatus?.message ||
          "Team registration is not available at this time.",
      );
      return;
    }

    // Check age eligibility
    if (!ageEligibility?.is_eligible) {
      setError(
        ageEligibility?.message ||
          "You are not eligible to participate due to age requirements.",
      );
      return;
    }

    // Check nationality eligibility
    if (!nationalityEligibility?.is_eligible) {
      setError(
        nationalityEligibility?.message ||
          "You are not eligible to participate due to nationality requirements.",
      );
      return;
    }

    setRegistration((prev) => ({ ...prev, flow: "join", step: 1 }));
  };

  // Check team name availability (simplified - no backend endpoint)
  const checkTeamNameAvailability = async (teamName: string) => {
    // Simple client-side validation since there's no backend endpoint
    // In a real app, you'd need to add this endpoint to the backend
    setTimeout(() => {
      const isAvailable =
        teamName.length >= 3 && !teamName.toLowerCase().includes("taken");
      setTeamNameAvailable(isAvailable);
    }, 500);
  };

  // Copy team ID to clipboard
  const copyTeamIdToClipboard = async () => {
    if (registration.teamData.teamId) {
      try {
        await navigator.clipboard.writeText(registration.teamData.teamId);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000); // Hide notification after 2 seconds
      } catch (err) {
        console.error("Failed to copy team ID:", err);
      }
    }
  };

  // Helper functions for step completion and time formatting
  const formatTimeSince = (date: Date | null): string => {
    if (!date) return "Not saved yet";

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60)
      return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""} ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  };

  const validateCurrentStep = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (registration.flow === "create") {
      switch (registration.step) {
        case 1: // Team Name & Manifesto
          if (!registration.teamData.name?.trim()) {
            errors.push("Team name is required");
          } else if (teamNameAvailable === false) {
            errors.push(
              "Team name is not available. Please choose a different name",
            );
          }
          if (!registration.teamData.manifesto?.trim())
            errors.push("Team manifesto is required");
          break;

        case 2: // League Selection
          if (!registration.teamData.league)
            errors.push("League selection is required");
          break;

        case 3: // Category Selection
          if (!registration.teamData.category)
            errors.push("Category selection is required");
          break;

        case 4: // Team Summary (all fields complete)
          if (!registration.teamData.name?.trim())
            errors.push("Team name is required");
          if (!registration.teamData.manifesto?.trim())
            errors.push("Team manifesto is required");
          if (!registration.teamData.league)
            errors.push("League selection is required");
          if (!registration.teamData.category)
            errors.push("Category selection is required");
          break;
      }
    } else if (registration.flow === "join") {
      // Join team validation
      if (!registration.teamData.teamId?.trim())
        errors.push("Team ID is required");
    }

    return { isValid: errors.length === 0, errors };
  };

  const getStepCompletionStatus = (): boolean => {
    const validation = validateCurrentStep();
    return validation.isValid;
  };

  const getStepStatusMessage = (): string => {
    const isCompleted = getStepCompletionStatus();
    if (isCompleted) {
      return "You have completed this step.";
    } else {
      const validation = validateCurrentStep();
      const errorCount = validation.errors.length;
      return `${errorCount} field${errorCount > 1 ? "s" : ""} need${
        errorCount === 1 ? "s" : ""
      } to be completed.`;
    }
  };

  const getStepSpecificTips = () => {
    if (registration.flow === "create") {
      switch (registration.step) {
        case 1:
          return {
            title: "Tips for a better adventure",
            content: [
              "Team name must be unique and not duplicated in leagues and categories",
              "Team name should start and end with the name of a constellation",
              "Your team manifesto should clearly express your mission and values",
              "Make your manifesto inspiring and memorable",
            ],
          };
        case 2:
          return {
            title: "Tips for a better adventure",
            content: [
              "Each contestant can only take part in one league only (Student or Industry Practitioner).",
              `You may be asked to provide a valid student card with an expiry date after 1 Jan ${COMPETITION_YEAR} in order to be confirmed as eligible to compete in the Student league.`,
              "Marketers category is for full-time client-side employees only; others must switch categories.",
            ],
          };
        case 3:
          return {
            title: "Tips for a better adventure",
            content: [
              "Choose the category that best matches your team's expertise",
              "Digital: Focus on digital marketing, social media, and online campaigns",
              "Film: Creative video content, storytelling, and visual communication",
              "Marketers: Traditional and integrated marketing approaches",
            ],
          };
        case 4:
          return {
            title: "Tips for a better adventure",
            content: [
              "Review all your selections carefully before creating the team",
              "Once created, you'll receive a unique Team ID for your partner to join",
              "Your team information can be edited later if needed",
              "Make sure all details accurately represent your team's vision",
            ],
          };
      }
    } else if (registration.flow === "join") {
      return {
        title: "Tips for a better adventure",
        content: [
          "Enter the exact Team ID provided by your team leader",
          "Team IDs are case-sensitive and must be entered exactly as shown",
          "Click Search to verify the team exists before joining",
          "You can only be a member of one team at a time",
        ],
      };
    }

    return {
      title: "Tips for a better adventure",
      content: ["Fields marked with * are required"],
    };
  };

  // Error Display Component for handling multiline errors
  const ErrorDisplay = ({ error }: { error: string }) => {
    if (!error) return null;

    return (
      <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
        {error.split("\n").map((line, index) => (
          <div key={index} className={index > 0 ? "mt-2" : ""}>
            {line}
          </div>
        ))}
      </div>
    );
  };

  const gotoYourTeam = () => {
    navigate("/dashboard/team");
  };

  // Guidelines Status Component
  const GuidelinesStatus = () => {
    const isCompleted = getStepCompletionStatus();
    const statusMessage = getStepStatusMessage();
    const timeMessage = formatTimeSince(lastSavedTime);

    return (
      <div className="flex items-start space-x-3 mt-4 sm:mt-6">
        <div
          className={`w-5 h-5 ${
            isCompleted ? "bg-green-600" : "bg-yellow-500"
          } rounded-full flex items-center justify-center`}
        >
          <span className="text-white text-xs">{isCompleted ? "✓" : "!"}</span>
        </div>
        <div className="text-sm">
          <div
            className={`font-medium ${
              isCompleted ? "text-green-800" : "text-yellow-800"
            }`}
          >
            {statusMessage}
          </div>
          <div className={isCompleted ? "text-green-600" : "text-yellow-600"}>
            {timeMessage}
          </div>
        </div>
      </div>
    );
  };

  // Reusable Action Buttons Component
  const ActionButtons = ({ className = "" }) => {
    const getButtonsForCurrentState = () => {
      if (registration.flow === "create") {
        switch (registration.step) {
          case 1:
            return (
              <>
                <button
                  onClick={prevStep}
                  className="flex-1 px-3 sm:px-4 py-3 bg-[#B5AEAD] text-white rounded-lg opacity-50 cursor-not-allowed font-medium text-sm min-h-[44px] touch-manipulation"
                  disabled
                >
                  Back
                </button>
                <button
                  onClick={nextStep}
                  className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm min-h-[44px] touch-manipulation"
                >
                  Next
                </button>
              </>
            );
          case 2:
          case 3:
            return (
              <>
                <button
                  onClick={prevStep}
                  className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm min-h-[44px] touch-manipulation"
                >
                  Back
                </button>
                <button
                  onClick={nextStep}
                  className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm min-h-[44px] touch-manipulation"
                >
                  Next
                </button>
              </>
            );
          case 4:
            return (
              <>
                <button
                  onClick={prevStep}
                  className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm min-h-[44px] touch-manipulation"
                >
                  Back
                </button>
                <button
                  onClick={createTeam}
                  disabled={loading}
                  className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm min-h-[44px] touch-manipulation disabled:opacity-50"
                >
                  {loading ? "Creating..." : "Create Team"}
                </button>
              </>
            );
        }
      } else if (registration.flow === "join") {
        return (
          <>
            <button
              onClick={prevStep}
              className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm min-h-[44px] touch-manipulation"
            >
              Back
            </button>
            <button
              onClick={joinTeam}
              disabled={loading || !registration.searchResults}
              className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm min-h-[44px] touch-manipulation disabled:opacity-50"
            >
              {loading ? "Joining..." : "Join Team"}
            </button>
          </>
        );
      }
      return null;
    };

    return (
      <div className={`flex gap-2 sm:gap-3 ${className}`}>
        {getButtonsForCurrentState()}
      </div>
    );
  };

  // Search team by ID using the details endpoint
  const searchTeamById = async (teamId: string) => {
    if (teamId.length < 3) return;

    try {
      const response = await TeamAPI.getTeamDetails(teamId);

      if (response.success && response.team) {
        // Set search results with actual team data
        setRegistration((prev) => ({
          ...prev,
          searchResults: {
            teamId: response.team.team_id,
            teamName: response.team.team_name,
            createdBy:
              response.team.members?.[0]?.user?.full_name || "Team Creator",
            league: response.team.league?.name || "Unknown",
            category: response.team.category?.name || "Unknown",
          },
        }));
        setError(""); // Clear any previous errors
      } else {
        // Clear search results if team not found
        setRegistration((prev) => ({
          ...prev,
          searchResults: undefined,
        }));
      }
    } catch (err: any) {
      console.error("Team search failed:", err);
      // Clear search results on error
      setRegistration((prev) => ({
        ...prev,
        searchResults: undefined,
      }));
      // Set error for manual search
      if (err.message.includes("404")) {
        setError("Team not found. Please check the Team ID and try again.");
      } else {
        setError("Failed to search for team. Please try again.");
      }
    }
  };

  // Handle input changes
  const handleTeamDataChange = (field: keyof TeamData, value: string) => {
    setRegistration((prev) => ({
      ...prev,
      teamData: { ...prev.teamData, [field]: value },
    }));

    // Check team name availability when name changes (for create team flow)
    if (field === "name" && value.trim() && registration.flow === "create") {
      checkTeamNameAvailability(value);
    }

    // Clear search results when teamId changes (but don't auto-search)
    if (field === "teamId" && registration.flow === "join") {
      setRegistration((prev) => ({
        ...prev,
        searchResults: undefined,
      }));
    }
  };

  // Save progress for intermediate steps
  const saveProgress = async () => {
    setLoading(true);
    setError("");

    try {
      // Just simulate saving progress for now
      await new Promise((resolve) => setTimeout(resolve, 500));
      // For intermediate steps, just move to next step
      nextStep();
    } catch (err: any) {
      setError(err.message || "Failed to save progress");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to extract specific validation errors
  const getValidationErrorMessage = (errors: any): string => {
    if (!errors || typeof errors !== "object") {
      return "Validation failed";
    }

    // Extract the first error message from any field
    for (const field in errors) {
      if (
        errors[field] &&
        Array.isArray(errors[field]) &&
        errors[field].length > 0
      ) {
        return errors[field][0]; // Return the first error message
      }
    }

    return "Validation failed";
  };

  // Create team functionality (called on final step)
  const createTeam = async () => {
    if (
      !registration.teamData.name ||
      !registration.teamData.manifesto ||
      !registration.teamData.league ||
      !registration.teamData.category
    ) {
      setError("Please fill in all required fields");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const leagueId =
        leagueMap[registration.teamData.league as keyof typeof leagueMap];
      const categoryId =
        categoryMap[registration.teamData.category as keyof typeof categoryMap];

      if (!leagueId || !categoryId) {
        setError("Invalid league or category selected");
        return;
      }

      const response = await TeamAPI.createTeam({
        team_name: registration.teamData.name,
        description: registration.teamData.manifesto,
        league_id: leagueId,
        category_id: categoryId,
      });

      if (response.success) {
        // Update state with team ID from response
        setRegistration((prev) => ({
          ...prev,
          teamData: {
            ...prev.teamData,
            teamId: response.team?.team_id,
          },
        }));
        setLastSavedTime(new Date()); // Update last saved time
        loadUserTeams(); // Refresh team data
        nextStep(); // Go to finish step
      } else {
        // Handle different error types
        if (
          response.error === "ELIGIBILITY_VIOLATION" &&
          response.errors &&
          Array.isArray(response.errors)
        ) {
          // Create a comprehensive error message for eligibility violations
          const constraintErrors = response.errors.join("\n\n");
          let detailedMessage = `${response.message}\n\n${constraintErrors}`;

          // Add existing teams information if available
          if (response.existing_teams && response.existing_teams.length > 0) {
            detailedMessage += "\n\nYour current teams:";
            response.existing_teams.forEach((team: any) => {
              detailedMessage += `\n• ${team.team_name} (${team.league} - ${team.category})`;
            });
          }

          setError(detailedMessage);
        } else if (response.errors) {
          // Handle validation errors
          const errorMessage = getValidationErrorMessage(response.errors);
          setError(errorMessage);
        } else {
          // Handle other error types
          setError(response.message || "Failed to create team");
        }
      }
    } catch (err: any) {
      if (err.message.includes("Token expired")) {
        setError("Your session expired. Please try creating the team again.");
      } else if (err.message.includes("Authentication failed")) {
        setError("Please log in again to continue.");
      } else if (err.errorType === "ELIGIBILITY_VIOLATION") {
        // Handle league and category constraint violations with detailed information
        setError(err.message);
      } else if (err.message.includes("already a member of a team")) {
        setError(
          "You are already a member of a team. You can only be in one team at a time.",
        );
      } else {
        setError(err.message || "Failed to create team");
      }
    } finally {
      setLoading(false);
    }
  };

  // Render initial step (choose flow)
  const renderInitialStep = () => (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-0">
      {/* Registration Status Header */}
      <div className="mb-8">{getRegistrationStatusMessage()}</div>

      {/* Steps Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Step 1 - Active */}
        <div className="bg-white rounded-lg border-2 border-gray-300 p-6 h-full flex flex-col">
          <div className="text-center flex-1 flex flex-col">
            <div className="text-4xl font-bold text-gray-800 mb-2">1</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Your first step to find out your constellation
            </h3>
            <div className="h-16 flex items-center justify-center mb-4">
              <p className="text-sm text-gray-600">
                Please enter team ID (team ID is unique and is only given when a
                new team is created)
              </p>
            </div>

            {/* Separator Line */}
            <hr className="w-full border-gray-300 mb-4" />

            <div className=" mt-auto">
              <p className="text-xs text-gray-500">
                You are a complete beginner, that's ok. Let's start the journey
              </p>
              <button
                onClick={selectCreateTeam}
                className="w-full bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white py-3 px-4 rounded-lg hover:opacity-90 transition-opacity font-medium mt-2"
              >
                Create new team
              </button>
              <p className="text-xs text-gray-500 mt-4">
                Somebody has a great team already. Let's join them
              </p>
              <button
                onClick={selectJoinTeam}
                className="w-full bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white py-3 px-4 rounded-lg hover:opacity-90 transition-opacity font-medium mt-2"
              >
                Join your team
              </button>
            </div>
          </div>
        </div>

        {/* Step 2 - Disabled */}
        <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-6 h-full flex flex-col">
          <div className="text-center opacity-50 flex flex-col">
            <div className="text-4xl font-bold text-gray-400 mb-2">2</div>
            <h3 className="text-lg font-semibold text-gray-400 mb-2">
              Tell us which is your orbit
            </h3>
            <div className="h-16 flex items-center justify-center mb-4">
              <p className="text-sm text-gray-400">
                Wherever you come from, now pick your orbit and ready for the
                journey into creative universe
              </p>
            </div>

            {/* Separator Line */}
            <hr className="w-full border-gray-400 mb-4" />

            <div className=" mt-auto">
              <p className="text-xs text-gray-400">
                Move to the zone that can help you pick your orbit
              </p>
              <button
                disabled
                className="w-full bg-gray-300 text-gray-500 py-3 px-4 rounded-lg font-medium cursor-not-allowed mt-2"
              >
                Create new team
              </button>
            </div>
          </div>
        </div>

        {/* Step 3 - Disabled */}
        <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-6 h-full flex flex-col">
          <div className="text-center opacity-50 flex flex-col">
            <div className="text-4xl font-bold text-gray-400 mb-2">3</div>
            <h3 className="text-lg font-semibold text-gray-400 mb-2">
              Put your feet on your battleground
            </h3>
            <div className="h-16 flex items-center justify-center mb-4">
              <p className="text-sm text-gray-400">
                Pick your category, then set your spirit steady for the upcoming
                battle
              </p>
            </div>

            {/* Separator Line */}
            <hr className="w-full border-gray-400 mb-4" />

            <div className=" mt-auto">
              <p className="text-xs text-gray-400">
                Jump to the place that you can choose your category
              </p>
              <button
                disabled
                className="w-full bg-gray-300 text-gray-500 py-3 px-4 rounded-lg font-medium cursor-not-allowed mt-2"
              >
                Category
              </button>
            </div>
          </div>
        </div>

        {/* Step 4 - Disabled */}
        <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-6 h-full flex flex-col">
          <div className="text-center opacity-50 flex flex-col">
            <div className="text-4xl font-bold text-gray-400 mb-2">4</div>
            <h3 className="text-lg font-semibold text-gray-400 mb-2">
              Finish your team information
            </h3>
            <div className="h-16 flex items-center justify-center mb-4">
              <p className="text-sm text-gray-400">
                View all information of your team here and it have a code that
                can help you call out your teamate
              </p>
            </div>

            {/* Separator Line */}
            <hr className="w-full border-gray-400 mb-4" />

            <div className=" mt-auto">
              <p className="text-xs text-gray-400">
                All team information will be displayed here
              </p>
              <button
                disabled
                className="w-full bg-gray-300 text-gray-500 py-3 px-4 rounded-lg font-medium cursor-not-allowed mt-2"
              >
                Team information
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render create team step 1
  const renderCreateTeamStep1 = () => (
    <div className="flex flex-col lg:flex-row gap-8 bg-[#EDEDED] rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm">
      {/* Left Column - Form */}
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-6 lg:mb-8">
          <div className="w-5 sm:w-6 flex items-center justify-center">
            <img src={UsersImg} alt="User Profile" className="w-full" />
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            Team information
          </h2>
        </div>

        <div className="space-y-4 mb-6">
          <div>
            <FormInput
              label="Team name"
              placeholder="Bun Vit"
              value={registration.teamData.name}
              onChange={(value) => handleTeamDataChange("name", value)}
              required
            />
            {teamNameAvailable === true && (
              <div className="flex items-center gap-2 mt-2">
                <svg
                  className="w-4 h-4 text-green-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm text-green-600">
                  This team name is available
                </span>
              </div>
            )}
            {teamNameAvailable === false && (
              <div className="flex items-center gap-2 mt-2">
                <svg
                  className="w-4 h-4 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm text-red-600">
                  This team name is not available
                </span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Team manifesto <span className="text-red-500">*</span>
            </label>
            <textarea
              placeholder="Tell us the story about your team name: English preferred · Max 30 words."
              value={registration.teamData.manifesto}
              onChange={(e) =>
                handleTeamDataChange("manifesto", e.target.value)
              }
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-none bg-gray-200"
            />
          </div>
        </div>

        <ErrorDisplay error={error} />
      </div>

      {/* Right Column - Guidelines */}
      <div className="hidden lg:block w-80 space-y-4 text-left">
        <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm h-fit">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">
            {getStepSpecificTips().title}
          </h3>

          <p className="text-sm text-gray-600 mb-2">
            Fields marked <span className="text-red-500">*</span> are required.
          </p>

          <div className="space-y-2 text-xs text-gray-600">
            {getStepSpecificTips().content.map((tip, index) => (
              <p key={index}>{tip}</p>
            ))}
          </div>

          {/* Completion Status */}
          <div
            className={`p-3 rounded-lg border ${
              getStepCompletionStatus()
                ? "bg-green-50 border-green-200"
                : "bg-yellow-50 border-yellow-200"
            }`}
          >
            <GuidelinesStatus />
          </div>
        </div>

        {/* Action Buttons - Desktop Only */}
        <ActionButtons className="hidden lg:flex" />
      </div>
    </div>
  );

  // Render category selection step
  const renderCategoryStep = () => (
    <div className="flex flex-col lg:flex-row gap-8 bg-[#EDEDED] rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm">
      {/* Left Column - Category Selection */}
      <div className="flex-1">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Marketers */}
          <div
            className={`border-2 rounded-lg p-6 transition-all h-full flex flex-col ${
              isFreelancer()
                ? "border-red-300 bg-red-50 cursor-not-allowed opacity-50"
                : registration.teamData.category === "marketers"
                  ? "border-gray-800 bg-white cursor-pointer"
                  : "border-dashed border-gray-300 bg-gray-50 cursor-pointer"
            }`}
            onClick={() => {
              if (!isFreelancer()) {
                handleTeamDataChange("category", "marketers");
              }
            }}
          >
            <div
              className={`text-center flex-1 flex flex-col ${
                isFreelancer()
                  ? "opacity-30"
                  : registration.teamData.category === "marketers"
                    ? "opacity-100"
                    : "opacity-60"
              }`}
            >
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <img src={cateMakerter} className="w-full" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Marketers
              </h3>
              {/* Separator Line */}
              <hr className="w-full border-gray-400 mb-4" />

              <div className="text-sm text-gray-600 text-left space-y-2 flex-1 text-center">
                {isFreelancer() ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700">
                    <div className="flex items-center justify-center mb-2">
                      <svg
                        className="h-5 w-5 text-red-500 mr-2"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="font-medium text-sm">
                        Restricted Category
                      </span>
                    </div>
                    <p className="text-xs text-center">
                      Freelancer does not compete in the Industry Practitioner
                      team - Marketers category
                    </p>
                  </div>
                ) : (
                  <p>
                    Within 24 hours, each team has to develop a marketing
                    campaign to solve a business challenge creatively in 1
                    format: A 10 slide presentation, pdf with maximum 10MB
                    (including 1 executive summary slide).
                  </p>
                )}
              </div>

              {isFreelancer() ? (
                <div className="w-full mt-6 py-3 px-4 rounded-lg font-medium bg-red-100 text-red-700 border border-red-300 text-center text-sm">
                  Not available for Freelancers
                </div>
              ) : (
                <button
                  className={`w-full mt-6 py-3 px-4 rounded-lg font-medium transition-colors ${
                    registration.teamData.category === "marketers"
                      ? "bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white"
                      : "bg-gray-300 text-gray-600"
                  }`}
                >
                  Select
                </button>
              )}
            </div>
          </div>

          {/* Digital */}
          <div
            className={`border-2 rounded-lg p-6 cursor-pointer transition-all h-full flex flex-col ${
              registration.teamData.category === "digital"
                ? "border-gray-800 bg-white"
                : "border-dashed border-gray-300 bg-gray-50"
            }`}
            onClick={() => handleTeamDataChange("category", "digital")}
          >
            <div
              className={`text-center flex-1 flex flex-col ${
                registration.teamData.category === "digital"
                  ? "opacity-100"
                  : "opacity-60"
              }`}
            >
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <img src={cateDigital} className="w-full" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Digital
              </h3>
              {/* Separator Line */}
              <hr className="w-full border-gray-400 mb-4" />

              <div className="text-sm text-gray-600 text-left space-y-2 flex-1 text-center">
                <p>
                  Within 24 hours, each team has to use their creativity and
                  strategic skills to develop an integrated digitally-led
                  campaign in 1 format: A horizontal 1-sided A3 page, pdf with
                  maximum 10MB. 
                </p>
              </div>

              <button
                className={`w-full mt-6 py-3 px-4 rounded-lg font-medium transition-colors ${
                  registration.teamData.category === "digital"
                    ? "bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white"
                    : "bg-gray-300 text-gray-600"
                }`}
              >
                Select
              </button>
            </div>
          </div>

          {/* Film */}
          <div
            className={`border-2 rounded-lg p-6 cursor-pointer transition-all h-full flex flex-col ${
              registration.teamData.category === "film"
                ? "border-gray-800 bg-white"
                : "border-dashed border-gray-300 bg-gray-50"
            }`}
            onClick={() => handleTeamDataChange("category", "film")}
          >
            <div
              className={`text-center flex-1 flex flex-col ${
                registration.teamData.category === "film"
                  ? "opacity-100"
                  : "opacity-60"
              }`}
            >
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <img src={cateFlim} className="w-full" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Film</h3>

              {/* Separator Line */}
              <hr className="w-full border-gray-400 mb-4" />

              <div className="text-sm text-gray-600 text-left space-y-2 flex-1 text-center">
                <p>
                  Within 48 hours, each team has to create a creative film of up
                  to 60 seconds (maximum 100MB) and a PDF of up to 10MB
                  summarizing the film.
                </p>
              </div>

              <button
                className={`w-full mt-6 py-3 px-4 rounded-lg font-medium transition-colors ${
                  registration.teamData.category === "film"
                    ? "bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white"
                    : "bg-gray-300 text-gray-600"
                }`}
              >
                Select
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Guidelines */}
      <div className="hidden lg:block w-80 space-y-4 text-left">
        <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm h-fit">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
            {getStepSpecificTips().title}
          </h3>

          <p className="text-sm text-gray-600 mb-3">
            Fields marked <span className="text-red-500">*</span> are required.
          </p>

          <div className="space-y-3 text-sm text-gray-600">
            {getStepSpecificTips().content.map((tip, index) => (
              <p key={index}>{tip}</p>
            ))}
          </div>

          {/* Completion Status */}
          <div
            className={`p-3 rounded-lg border ${
              getStepCompletionStatus()
                ? "bg-green-50 border-green-200"
                : "bg-yellow-50 border-yellow-200"
            }`}
          >
            <GuidelinesStatus />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 sm:gap-3">
          <button
            onClick={prevStep}
            className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm min-h-[44px]"
          >
            Back
          </button>
          <button
            onClick={createTeam}
            className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm min-h-[44px]"
          >
            Create Team
          </button>
        </div>
      </div>
    </div>
  );

  // Render finish step
  const renderFinishStep = () => (
    <div className="flex flex-col lg:flex-row gap-8 bg-[#EDEDED] rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm">
      {/* Left Column - Team Summary */}
      <div className="flex-1">
        {/* Team Creation Completed Card */}
        <div className="bg-white border-2 border-black rounded-lg p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            <div className="flex-shrink-0">
              <img src={iconLogo} className="w-16 h-16" />
            </div>
            <div className="hidden sm:block w-px h-16 bg-black"></div>
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 items-center">
              <div className="text-center sm:text-left">
                <h3 className="hidden sm:block text-lg font-semibold text-gray-800">
                  Team
                  <br />
                  creation
                  <br />
                  completed
                </h3>
                <h3 className="block sm:hidden text-lg font-semibold text-gray-800">
                  Team creation completed
                </h3>
              </div>
              <div className="sm:col-span-2">
                <p className="text-sm text-gray-600 leading-relaxed text-center sm:text-left sm:max-w-md">
                  A confirmation email has been sent to your inbox. You need one
                  more team member for your team to be confirmed.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Team Information Form */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Team name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Team name
            </label>
            <input
              type="text"
              value={registration.teamData.name || "Name"}
              readOnly
              className="w-full px-3 py-2 border-2 border-black rounded-lg bg-transparent text-black font-bold"
            />
          </div>

          {/* League */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              League
            </label>
            <input
              type="text"
              value={
                registration.teamData.league === "student"
                  ? "Student"
                  : registration.teamData.league === "professional"
                    ? "Industry Practitioner"
                    : "Student"
              }
              readOnly
              className="w-full px-3 py-2 border-2 border-black rounded-lg bg-transparent text-black font-bold"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Category
            </label>
            <input
              type="text"
              value={
                registration.teamData.category
                  ? registration.teamData.category.charAt(0).toUpperCase() +
                    registration.teamData.category.slice(1)
                  : "Film"
              }
              readOnly
              className="w-full px-3 py-2 border-2 border-black rounded-lg bg-transparent text-black font-bold"
            />
          </div>
        </div>

        {/* Team ID Row - Same columns as above */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Team ID in first column (same as Team name) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Team ID
            </label>
            <input
              type="text"
              value={registration.teamData.teamId || ""}
              readOnly
              className="w-full px-3 py-2 border-2 border-black rounded-lg bg-transparent text-black font-bold"
            />
          </div>

          {/* Select button in second column (same as League) */}
          <div className="flex items-end">
            <button
              onClick={copyTeamIdToClipboard}
              disabled={!registration.teamData.teamId}
              className="w-full bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white py-2 px-6 rounded-lg hover:opacity-90 transition-opacity font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {copySuccess ? "Copied!" : "Copy Team ID"}
            </button>
          </div>

          {/* Empty third column */}
          <div></div>
        </div>

        {copySuccess && (
          <div className="mt-6 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            Team ID copied to clipboard!
          </div>
        )}

        <ErrorDisplay error={error} />
      </div>

      {/* Right Column - Guidelines */}
      <div className="hidden lg:block w-80 space-y-4 text-left">
        <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm h-fit">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
            {getStepSpecificTips().title}
          </h3>

          <p className="text-sm text-gray-600 mb-3">
            Fields marked <span className="text-red-500">*</span> are required.
          </p>

          <div className="space-y-3 text-sm text-gray-600">
            {getStepSpecificTips().content.map((tip, index) => (
              <p key={index}>{tip}</p>
            ))}
          </div>

          {/* Completion Status */}
          <div
            className={`p-3 rounded-lg border ${
              getStepCompletionStatus()
                ? "bg-green-50 border-green-200"
                : "bg-yellow-50 border-yellow-200"
            }`}
          >
            <GuidelinesStatus />
          </div>
        </div>

        {/* Go to Team Button */}
        <button className="w-full bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white py-3 px-4 rounded-lg hover:opacity-90 transition-opacity font-medium">
          Go to Your Team
        </button>
      </div>
    </div>
  );

  // Render league selection step
  const renderLeagueStep = () => (
    <div className="flex flex-col lg:flex-row gap-8 bg-[#EDEDED] rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm">
      {/* Left Column - League Selection */}
      <div className="flex-1">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Student League */}
          <div
            className={`border-2 rounded-lg p-6 transition-all h-full flex flex-col ${
              userGraduationLoading
                ? "border-gray-300 bg-gray-100 cursor-wait opacity-70"
                : shouldUseIndustryPractitioner()
                  ? "border-red-300 bg-red-50 cursor-not-allowed opacity-50"
                  : registration.teamData.league === "student"
                    ? "border-gray-800 bg-white cursor-pointer"
                    : "border-dashed border-gray-300 bg-gray-50 cursor-pointer"
            }`}
            onClick={() => {
              if (!userGraduationLoading && !shouldUseIndustryPractitioner()) {
                handleTeamDataChange("league", "student");
              }
            }}
          >
            <div
              className={`text-center flex-1 flex flex-col ${
                shouldUseIndustryPractitioner()
                  ? "opacity-30"
                  : registration.teamData.league === "student"
                    ? "opacity-100"
                    : "opacity-60"
              }`}
            >
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Student
              </h3>
              <p className="text-sm text-gray-600 mb-4">league</p>
              {/* Separator Line */}
              <hr className="w-full border-gray-400 mb-4" />

              <div className="space-y-3 text-sm text-gray-600 text-left flex-1">
                {userGraduationLoading ? (
                  <div className="text-center space-y-2">
                    <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 mb-2">
                      <svg
                        className="w-3 h-3 mr-1 animate-spin"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Checking eligibility...
                    </div>
                    <p className="text-xs text-center">
                      Verifying your graduation date to determine league
                      eligibility.
                    </p>
                  </div>
                ) : shouldUseIndustryPractitioner() ? (
                  <div className="text-center space-y-2">
                    <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 mb-2">
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.485 3.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 3.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Restricted League
                    </div>
                    <p className="text-xs text-center">
                      Your graduation date ({userGraduationDate}) is before
                      01/01/{COMPETITION_YEAR}. Please select Industry
                      Practitioner league.
                    </p>
                  </div>
                ) : (
                  <>
                    <p>
                      Must be a current full-time student at an accredited
                      college/university, not yet graduated and without industry
                      experience.
                    </p>
                    <p>
                      Teams with 1 full-time Student and 1 Industry Practitioner
                      are not eligible.
                    </p>
                    <p>
                      Students who will graduate before 1 Jan {COMPETITION_YEAR}{" "}
                      must register in the Industry Practitioner league.
                    </p>
                  </>
                )}
              </div>

              {userGraduationLoading ? (
                <div className="w-full mt-6 py-3 px-4 rounded-lg font-medium bg-gray-200 text-gray-500 cursor-wait">
                  Checking...
                </div>
              ) : shouldUseIndustryPractitioner() ? (
                <div className="w-full mt-6 py-3 px-4 rounded-lg font-medium bg-gray-300 text-gray-500 cursor-not-allowed">
                  Not available
                </div>
              ) : (
                <button
                  className={`w-full mt-6 py-3 px-4 rounded-lg font-medium transition-colors ${
                    registration.teamData.league === "student"
                      ? "bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white"
                      : "bg-gray-300 text-gray-600"
                  }`}
                >
                  Select
                </button>
              )}
            </div>
          </div>

          {/* Industry Practitioner League */}
          <div
            className={`border-2 rounded-lg p-6 cursor-pointer transition-all h-full flex flex-col ${
              registration.teamData.league === "professional"
                ? "border-gray-800 bg-white"
                : "border-dashed border-gray-300 bg-gray-50"
            }`}
            onClick={() => handleTeamDataChange("league", "professional")}
          >
            <div className="text-center flex-1 flex flex-col">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Industry Practitioner
              </h3>
              <p className="text-sm text-gray-600 mb-4">league</p>
              {/* Separator Line */}
              <hr className="w-full border-gray-400 mb-4" />
              <div className="space-y-3 text-sm text-gray-600 text-left flex-1">
                <p>
                  Full-time employee in advertising/marketing (agency or client
                  side), or freelancer in advertising/creative communication.
                </p>
                <p>
                  Be a student or intern graduating before 1 Jan{" "}
                  {COMPETITION_YEAR}.
                </p>
              </div>

              <button
                className={`w-full mt-6 py-3 px-4 rounded-lg font-medium transition-colors ${
                  registration.teamData.league === "professional"
                    ? "bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white"
                    : "bg-gray-300 text-gray-600"
                }`}
              >
                Select
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Guidelines */}
      <div className="hidden lg:block w-80 space-y-4 text-left">
        <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm h-fit">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
            {getStepSpecificTips().title}
          </h3>

          <p className="text-sm text-gray-600 mb-3">
            Fields marked <span className="text-red-500">*</span> are required.
          </p>

          <div className="space-y-3 text-sm text-gray-600">
            {getStepSpecificTips().content.map((tip, index) => (
              <p key={index}>{tip}</p>
            ))}
          </div>

          {/* Completion Status */}
          <div
            className={`p-3 rounded-lg border ${
              getStepCompletionStatus()
                ? "bg-green-50 border-green-200"
                : "bg-yellow-50 border-yellow-200"
            }`}
          >
            <GuidelinesStatus />
          </div>
        </div>

        {/* Action Buttons - Desktop Only */}
        <ActionButtons className="hidden lg:flex" />
      </div>
    </div>
  );

  // Handle team search (manual search button)
  const searchTeam = async () => {
    if (!registration.teamData.teamId) {
      setError("Please enter a team ID");
      return;
    }

    setLoading(true);
    setError("");

    // Use the same search function but with loading state
    await searchTeamById(registration.teamData.teamId);
    setLoading(false);
  };

  // Join team action
  const joinTeam = async () => {
    if (!registration.teamData.teamId || !registration.searchResults) {
      setError("Please search and select a team first");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await TeamAPI.joinTeam(registration.teamData.teamId);

      if (response.success) {
        // Update registration state with the joined team information
        setRegistration((prev) => ({
          ...prev,
          teamData: {
            ...prev.teamData,
            name: registration.searchResults?.teamName || "",
            league:
              (registration.searchResults?.league as
                | "student"
                | "professional") || "student",
            category:
              (registration.searchResults?.category as
                | "digital"
                | "film"
                | "marketers") || "digital",
          },
        }));

        loadUserTeams(); // Refresh team data
        nextStep(); // Go to finish step
      } else {
        setError(response.message || "Failed to join team");
      }
    } catch (err: any) {
      if (err.errorType === "ELIGIBILITY_VIOLATION") {
        // Handle league and category constraint violations with detailed information
        setError(err.message);
      } else {
        setError(err.message || "Failed to join team");
      }
    } finally {
      setLoading(false);
    }
  };

  // Render join team search step
  const renderJoinTeamSearch = () => {
    // If we haven't searched yet or no results
    if (!registration.searchResults) {
      return (
        <div className="flex flex-col lg:flex-row gap-8 bg-[#EDEDED] rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm">
          {/* Left Column - Search Form */}
          <div className="flex-1">
            {/* Header Row - Icon, Title, Input, and Button on same row */}
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 flex items-center justify-center">
                  <img src={UsersImg} alt="User Profile" className="w-full" />
                </div>
                <h2 className="text-sm font-semibold text-gray-900 whitespace-nowrap text-left">
                  Join team
                  <br />
                  with Team ID
                </h2>
              </div>

              <div className="flex gap-3 flex-1">
                <div className="flex-1">
                  <FormInput
                    label=""
                    placeholder="Enter Team ID"
                    value={registration.teamData.teamId || ""}
                    onChange={(value) => handleTeamDataChange("teamId", value)}
                  />
                </div>
                <button
                  onClick={searchTeam}
                  disabled={loading || !registration.teamData.teamId}
                  className="bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white py-2 px-6 rounded-lg hover:opacity-90 transition-opacity font-medium disabled:opacity-50"
                >
                  {loading ? "Searching..." : "Search"}
                </button>
              </div>
            </div>

            {/* Search Area - Equal height with right column */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center h-[400px] flex items-center justify-center">
              <div className="text-center">
                <p className="text-gray-500 mb-2">
                  Please enter team ID to search for detail information
                </p>
                <p className="text-xs text-gray-400">
                  Enter the Team ID and click Search to find the team
                </p>
              </div>
            </div>

            <ErrorDisplay error={error} />
          </div>

          {/* Right Column - Guidelines */}
          <div className="hidden lg:block w-80 space-y-4 text-left">
            <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm flex flex-col">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
                Tips for a better adventure
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                Fields marked <span className="text-red-500">*</span> are
                required.
              </p>

              <div className="space-y-2 text-xs text-gray-600">
                <p>
                  Team name must be unique and not duplicated in leagues and
                  categories.
                </p>
                <p>
                  Team name should start and end with the name of a
                  constellation that you like.
                </p>
                <p>Do not use numbers and special characters for team name.</p>
                <p>
                  Copy and share the team ID with your teammate, then wait for
                  their confirmation to complete your 2-member team.
                </p>
              </div>

              {/* Completion Status */}
              <div className="mt-6 p-3 rounded-lg border bg-yellow-50 border-yellow-200">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">!</span>
                  </div>
                  <div className="text-sm">
                    <div className="font-medium text-yellow-800">
                      You have completed this step.
                    </div>
                    <div className="text-yellow-600">Last saved 3 days ago</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Join Button */}
            <button
              disabled
              className="w-full bg-gray-300 text-gray-500 py-3 px-4 rounded-lg font-medium cursor-not-allowed"
            >
              Join
            </button>
          </div>
        </div>
      );
    }

    // Show search results / confirm step
    return (
      <div className="flex flex-col lg:flex-row gap-8 bg-[#EDEDED] rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm">
        {/* Left Column - Team Details */}
        <div className="flex-1">
          {/* Header Row - Same as search state */}
          <div className="flex items-center gap-6 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 flex items-center justify-center">
                <img src={UsersImg} alt="User Profile" className="w-full" />
              </div>
              <h2 className="text-sm font-semibold text-gray-900 whitespace-nowrap text-left">
                Join team
                <br />
                with Team ID
              </h2>
            </div>

            <div className="flex gap-3 flex-1">
              <div className="flex-1">
                <FormInput
                  label=""
                  placeholder="Team ID"
                  value={registration.teamData.teamId || ""}
                  onChange={(value) => handleTeamDataChange("teamId", value)}
                />
              </div>
              <button
                onClick={searchTeam}
                disabled={loading}
                className="bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white py-2 px-6 rounded-lg hover:opacity-90 transition-opacity font-medium disabled:opacity-50"
              >
                {loading ? "Searching..." : "Search"}
              </button>
            </div>
          </div>

          {/* Team Found Results */}
          <div className="border-2 border-solid border-green-300 bg-green-50 rounded-lg p-6 h-[400px] flex flex-col">
            <div className="flex items-center justify-center gap-2 mb-4">
              <svg
                className="w-5 h-5 text-green-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-sm text-green-700 font-medium">
                Team Found!
              </span>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 text-center mb-6">
              {registration.searchResults.teamName}
            </h3>

            <div className="grid grid-cols-2 gap-4 flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                  Team ID
                </label>
                <input
                  type="text"
                  value={registration.searchResults.teamId}
                  readOnly
                  className="w-full px-3 py-2 border-2 border-black rounded-lg bg-transparent text-black font-bold"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                  Created by
                </label>
                <input
                  type="text"
                  value={registration.searchResults.createdBy}
                  readOnly
                  className="w-full px-3 py-2 border-2 border-black rounded-lg bg-transparent text-black font-bold"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                  League
                </label>
                <input
                  type="text"
                  value={registration.searchResults.league}
                  readOnly
                  className="w-full px-3 py-2 border-2 border-black rounded-lg bg-transparent text-black font-bold"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                  Category
                </label>
                <input
                  type="text"
                  value={registration.searchResults.category}
                  readOnly
                  className="w-full px-3 py-2 border-2 border-black rounded-lg bg-transparent text-black font-bold"
                />
              </div>
            </div>
          </div>

          <ErrorDisplay error={error} />
        </div>

        {/* Right Column - Guidelines */}
        <div className="hidden lg:block w-80 space-y-4 text-left">
          <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm flex flex-col">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
              Tips for a better adventure
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Fields marked <span className="text-red-500">*</span> are
              required.
            </p>

            <div className="space-y-3 text-sm text-gray-600 flex-1">
              <p>
                Team name must be unique and not duplicated in leagues and
                categories
              </p>
              <p>
                Team name should start and end with the name of a constellation
                that you like
              </p>
              <p>Do not use numbers and special characters for team name</p>
              <p>
                Copy and share team id with teammates and wait for confirmation
                request to complete your 2-members team
              </p>
            </div>

            {/* Completion Status */}
            <div className="mt-6 p-3 rounded-lg border bg-yellow-50 border-yellow-200">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs">!</span>
                </div>
                <div className="text-sm">
                  <div className="font-medium text-yellow-800">
                    You have completed this step.
                  </div>
                  <div className="text-yellow-600">Last saved 3 days ago</div>
                </div>
              </div>
            </div>
          </div>

          {/* Join Button - Now enabled and highlighted */}
          <button
            onClick={joinTeam}
            disabled={loading}
            className="w-full bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white py-3 px-4 rounded-lg hover:opacity-90 transition-opacity font-medium disabled:opacity-50"
          >
            {loading ? "Joining..." : "Join"}
          </button>
        </div>
      </div>
    );
  };

  // Render join team finish step
  const renderJoinTeamFinish = () => (
    <div className="flex flex-col lg:flex-row gap-8 bg-[#EDEDED] rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm">
      {/* Left Column - Success Message */}
      <div className="flex-1">
        {/* Success Card */}
        <div className="bg-white border-2 border-gray-300 rounded-lg p-12 text-center">
          <div className="w-32 mx-auto mb-6 flex items-center justify-center">
            <img src={iconLogo} className="w-full" />
          </div>

          <h3 className="text-2xl font-bold text-gray-800 mb-4">
            YOU HAVE REQUESTED TO JOIN THE TEAM
          </h3>
          <p className="text-gray-600">
            Please wait for your teammate to confirm your slot.
          </p>
        </div>

        <ErrorDisplay error={error} />
      </div>

      {/* Right Column - Guidelines */}
      <div className="hidden lg:block w-80 space-y-4 text-left">
        <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm h-fit">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
            Tips for a better adventure
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            Fields marked <span className="text-red-500">*</span> are required.
          </p>

          <div className="space-y-3 text-sm text-gray-600">
            <p>
              Team name must be unique and not duplicated in leagues and
              categories
            </p>
            <p>
              Team name should start and end with the name of a constellation
              that you like
            </p>
            <p>Do not use numbers and special characters for team name</p>
            <p>
              Copy and share team id with teammates and wait for confirmation
              request to complete your 2-members team
            </p>
          </div>

          {/* Completion Status */}
          <div className="mt-6 p-3 rounded-lg border bg-yellow-50 border-yellow-200">
            <div className="flex items-start space-x-3">
              <div className="w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">!</span>
              </div>
              <div className="text-sm">
                <div className="font-medium text-yellow-800">
                  You have completed this step.
                </div>
                <div className="text-yellow-600">Last saved 3 days ago</div>
              </div>
            </div>
          </div>
        </div>

        {/* Go to Team Button */}
        <button
          onClick={gotoYourTeam}
          className="w-full bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white py-3 px-4 rounded-lg hover:opacity-90 transition-opacity font-medium"
        >
          Go to Your Team
        </button>
      </div>
    </div>
  );

  // Main render function
  const renderStepContent = () => {
    // Initial step - choose flow
    if (!registration.flow) {
      return renderInitialStep();
    }

    // Create team flow
    if (registration.flow === "create") {
      switch (registration.step) {
        case 1:
          return renderCreateTeamStep1();
        case 2:
          return renderLeagueStep();
        case 3:
          return renderCategoryStep();
        case 4:
          return renderFinishStep();
        default:
          return renderCreateTeamStep1();
      }
    }

    // Join team flow
    if (registration.flow === "join") {
      switch (registration.step) {
        case 1:
          return renderJoinTeamSearch();
        case 2:
          return renderJoinTeamFinish();
        default:
          return renderJoinTeamSearch();
      }
    }

    return null;
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-0">
        {/* Team Limit Reached Message */}
        {userTeams.length >= 2 ? (
          <div className="min-h-screen flex items-center justify-center">
            <div className="max-w-md w-full mx-auto text-center">
              <div className="bg-white rounded-lg shadow-lg p-8 border">
                <div className="mb-6">
                  <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                    <svg
                      className="w-8 h-8 text-orange-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Team Limit Reached
                  </h2>
                  <p className="text-gray-600 mb-4">
                    You already have 2 teams registered. The maximum number of
                    teams per participant is 2.
                  </p>
                  {userTeams.length > 0 && (
                    <div className="text-left bg-gray-50 rounded-lg p-4 mb-4">
                      <h3 className="font-semibold text-gray-800 mb-2">
                        Your Teams:
                      </h3>
                      <ul className="space-y-2">
                        {userTeams.map((team: any) => (
                          <li
                            key={team.id}
                            className="flex items-center justify-between text-sm"
                          >
                            <span className="font-medium">
                              {team.team_name}
                            </span>
                            <span
                              className={`px-2 py-1 rounded text-xs ${
                                team.team_status === "complete" ||
                                team.team_status === "competing"
                                  ? "bg-green-100 text-green-800"
                                  : team.team_status === "draft"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {team.team_status || "Draft"}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <p className="text-sm text-gray-500">
                    If you need to make changes to your existing teams, please
                    contact support.
                  </p>
                </div>
                <button
                  onClick={() => navigate("/dashboard")}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Step Progress Header - Only show when flow is selected and registration is allowed */}
            {registration.flow &&
              !registrationLoading &&
              (!registrationStatus || registrationStatus.can_register) && (
                <StepProgress
                  currentStep={registration.step}
                  totalSteps={getTotalSteps()}
                  stepLabels={getStepLabels()}
                  progressPercent={getProgressPercent()}
                />
              )}

            {/* Error Notification */}
            <ErrorNotification error={error} onClose={() => setError("")} />

            {/* Age Eligibility Notification */}
            {!ageEligibilityLoading &&
              ageEligibility &&
              !ageEligibility.is_eligible && (
                <div className="max-w-4xl mx-auto mb-6">
                  <div className="p-6 rounded-lg border-l-4 shadow-lg bg-red-50 border-red-400">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-6 w-6 text-red-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-4 flex-1">
                        <h3 className="text-lg font-semibold text-red-700">
                          Age Eligibility Requirement Not Met
                        </h3>
                        <div className="mt-3 text-sm text-red-600">
                          <p
                            className="mb-3"
                            dangerouslySetInnerHTML={{
                              __html: ageEligibility.message,
                            }}
                          ></p>

                          <div className="bg-white bg-opacity-50 rounded-md p-4 border border-red-200">
                            <h4 className="font-medium text-base mb-2">
                              Age Requirement Details
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex items-center space-x-2">
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                                <span className="font-medium">
                                  Required Birth Date:
                                </span>
                              </div>
                              <div className="ml-6">
                                <span className="font-medium">
                                  On or after:
                                </span>{" "}
                                June 25, 1996
                              </div>
                              {ageEligibility.birth_date && (
                                <div className="ml-6">
                                  <span className="font-medium">
                                    Your Birth Date:
                                  </span>{" "}
                                  {new Date(
                                    ageEligibility.birth_date,
                                  ).toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                  })}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="mt-4 text-xs opacity-75">
                            If you believe this is an error, please check your
                            profile information or contact the administrators
                            for assistance.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {/* Nationality Eligibility Notification */}
            {!nationalityEligibilityLoading &&
              nationalityEligibility &&
              !nationalityEligibility.is_eligible && (
                <div className="max-w-4xl mx-auto mb-6">
                  <div className="p-6 rounded-lg border-l-4 shadow-lg bg-red-50 border-red-400">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-6 w-6 text-red-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-4 flex-1">
                        <h3 className="text-lg font-semibold text-red-700">
                          Nationality Eligibility Requirement Not Met
                        </h3>
                        <div className="mt-3 text-sm text-red-600">
                          <p
                            className="mb-3"
                            dangerouslySetInnerHTML={{
                              __html: nationalityEligibility.message,
                            }}
                          ></p>

                          <div className="bg-white bg-opacity-50 rounded-md p-4 border border-red-200">
                            <h4 className="font-medium text-base mb-2">
                              Nationality Requirement Details
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex items-center justify-center space-x-2">
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                </svg>
                                <span className="font-medium">Required:</span>{" "}
                                Vietnamese
                              </div>
                              {nationalityEligibility.nationality && (
                                <div className="text-center">
                                  <span className="font-medium">
                                    Your Nationality:
                                  </span>{" "}
                                  {nationalityEligibility.nationality
                                    .charAt(0)
                                    .toUpperCase() +
                                    nationalityEligibility.nationality
                                      .slice(1)
                                      .replace("_", " ")}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="mt-4 text-xs opacity-75">
                            If you believe this is an error, please check your
                            profile information or contact the administrators
                            for assistance.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {/* Registration Status Notification - Enhanced */}
            {!registrationLoading &&
              registrationStatus &&
              !registrationStatus.can_register && (
                <div className="max-w-4xl mx-auto">
                  <div
                    className={`p-6 rounded-lg border-l-4 shadow-lg ${
                      registrationStatus.status === "closed"
                        ? "bg-red-50 border-red-400"
                        : registrationStatus.status === "upcoming"
                          ? "bg-yellow-50 border-yellow-400"
                          : "bg-gray-50 border-gray-400"
                    }`}
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        {registrationStatus.status === "closed" ? (
                          <svg
                            className="h-6 w-6 text-red-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : registrationStatus.status === "upcoming" ? (
                          <svg
                            className="h-6 w-6 text-yellow-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="h-6 w-6 text-gray-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      <div className="ml-4 flex-1">
                        <h3
                          className={`text-lg font-semibold ${
                            registrationStatus.status === "closed"
                              ? "text-red-700"
                              : registrationStatus.status === "upcoming"
                                ? "text-yellow-700"
                                : "text-gray-700"
                          }`}
                        >
                          {registrationStatus.status === "closed"
                            ? "Team Registration Period Has Ended"
                            : registrationStatus.status === "upcoming"
                              ? "Team Registration Opens Soon"
                              : "Team Registration Currently Unavailable"}
                        </h3>
                        <div
                          className={`mt-3 text-sm ${
                            registrationStatus.status === "closed"
                              ? "text-red-600"
                              : registrationStatus.status === "upcoming"
                                ? "text-yellow-600"
                                : "text-gray-600"
                          }`}
                        >
                          <p className="mb-3">{registrationStatus.message}</p>

                          {registrationStatus.registration_phase && (
                            <div className="bg-white bg-opacity-50 rounded-md p-4 border border-current border-opacity-20">
                              <h4 className="font-medium text-base mb-2">
                                {registrationStatus.registration_phase.name}
                              </h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex items-center space-x-2">
                                  <svg
                                    className="h-4 w-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                  </svg>
                                  <span className="font-medium">
                                    Registration Period:
                                  </span>
                                </div>
                                <div className="ml-6">
                                  <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-1 sm:space-y-0">
                                    <div>
                                      <span className="font-medium">
                                        Start:
                                      </span>{" "}
                                      {new Date(
                                        registrationStatus.registration_phase
                                          .start_time,
                                      ).toLocaleDateString("en-US", {
                                        weekday: "short",
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </div>
                                    <div>
                                      <span className="font-medium">End:</span>{" "}
                                      {new Date(
                                        registrationStatus.registration_phase
                                          .end_time,
                                      ).toLocaleDateString("en-US", {
                                        weekday: "short",
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {registrationStatus.registration_phase
                                .description && (
                                <div className="mt-3 text-xs opacity-75">
                                  {
                                    registrationStatus.registration_phase
                                      .description
                                  }
                                </div>
                              )}
                            </div>
                          )}

                          <div className="mt-4 text-xs opacity-75">
                            Please check back during the registration period or
                            contact the administrators if you have any
                            questions.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {/* Loading State for Registration Status, Age and Nationality Eligibility */}
            {(registrationLoading ||
              ageEligibilityLoading ||
              nationalityEligibilityLoading) && (
              <div className="mb-6">
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg">
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400"></div>
                    <p className="ml-3 text-sm text-blue-700">
                      {registrationLoading &&
                      ageEligibilityLoading &&
                      nationalityEligibilityLoading
                        ? "Checking registration status, age and nationality eligibility..."
                        : registrationLoading && ageEligibilityLoading
                          ? "Checking registration status and age eligibility..."
                          : registrationLoading && nationalityEligibilityLoading
                            ? "Checking registration status and nationality eligibility..."
                            : ageEligibilityLoading &&
                                nationalityEligibilityLoading
                              ? "Checking age and nationality eligibility..."
                              : registrationLoading
                                ? "Checking registration status..."
                                : ageEligibilityLoading
                                  ? "Checking age eligibility..."
                                  : "Checking nationality eligibility..."}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Main Content - Only show when registration is allowed AND age eligible AND nationality eligible */}
            {!registrationLoading &&
              !ageEligibilityLoading &&
              !nationalityEligibilityLoading &&
              (!registrationStatus || registrationStatus.can_register) &&
              (!ageEligibility || ageEligibility.is_eligible) &&
              (!nationalityEligibility || nationalityEligibility.is_eligible) &&
              renderStepContent()}

            {/* Mobile Action Buttons - Only visible on mobile/tablet and when registration is allowed AND age eligible AND nationality eligible */}
            {registration.flow &&
              !registrationLoading &&
              !ageEligibilityLoading &&
              !nationalityEligibilityLoading &&
              (!registrationStatus || registrationStatus.can_register) &&
              (!ageEligibility || ageEligibility.is_eligible) &&
              (!nationalityEligibility ||
                nationalityEligibility.is_eligible) && (
                <div className="lg:hidden mt-6 px-4">
                  <ActionButtons />
                </div>
              )}

            {/* Floating Help Button - Only visible on mobile/tablet and when registration is allowed AND age eligible AND nationality eligible */}
            {!registrationLoading &&
              !ageEligibilityLoading &&
              !nationalityEligibilityLoading &&
              (!registrationStatus || registrationStatus.can_register) &&
              (!ageEligibility || ageEligibility.is_eligible) &&
              (!nationalityEligibility ||
                nationalityEligibility.is_eligible) && (
                <div className="lg:hidden fixed bottom-6 right-6 z-40">
                  <button
                    onClick={() => setShowGuidelinesPopup(true)}
                    className="w-12 h-12 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center touch-manipulation"
                    aria-label="Show guidelines"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </button>
                </div>
              )}

            {/* Guidelines Popup - Mobile/Tablet Only */}
            {showGuidelinesPopup && (
              <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl p-4 sm:p-6 max-w-sm w-full max-h-[80vh] overflow-y-auto shadow-2xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {getStepSpecificTips().title}
                    </h3>
                    <button
                      onClick={() => setShowGuidelinesPopup(false)}
                      className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 touch-manipulation"
                      aria-label="Close guidelines"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-4 text-sm text-gray-600 mb-6">
                    <p>
                      Fields marked <span className="text-red-500">*</span> are
                      required.
                    </p>
                    {getStepSpecificTips().content.map((tip, index) => (
                      <p key={index}>{tip}</p>
                    ))}
                  </div>

                  {/* Completion Status */}
                  <div
                    className={`p-3 rounded-lg border mb-4 ${
                      getStepCompletionStatus()
                        ? "bg-green-50 border-green-200"
                        : "bg-yellow-50 border-yellow-200"
                    }`}
                  >
                    <GuidelinesStatus />
                  </div>
                </div>
              </div>
            )}

            <NotificationOverlay
              show={loading}
              message="Saving progress..."
              type="loading"
            />
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

export default RegistrationDashboard;
